import { useMutation, useQuery } from "@tanstack/react-query";
import { userApiService } from "../services/user.api";
import { UserHookParams, ApiResponse } from "../types/user.types";
import { showToastMessage } from "common/service/CommonService";

// ------------------------------------------------------------------
//-- USER
// ------------------------------------------------------------------

export const useUserService = ({
  id = undefined,
  searchText = null,
  pageNumber = 1,
  pageSize = 10,
  userid = null,
  enabledUserSelectAll = true,
  enabledUserSelect = false,
  enabledUserDropdown = false,
  enabledRoleDropdown = false,
  useLocalDropdown = false,
}: UserHookParams = {}) => {
  // ========================================
  // Queries
  // ========================================
  const SelectAllQuery = useQuery({
    queryKey: ["user-selectall", searchText, pageNumber, pageSize],
    queryFn: () =>
      userApiService.getSelectAllData(searchText, pageNumber, pageSize),
    enabled: enabledUserSelectAll,
    select: (response) => ({
      users: response?.data?.records || [],
      totalPages: Math.ceil(
        (response?.data?.records?.[0]?.totalcount ||
          response?.data?.records?.[0]?.total ||
          0) / pageSize
      ),
    }),
  });

  const SelectQuery = useQuery({
    queryKey: ["user-select", userid],
    queryFn: () => userApiService.getSelectData(userid),
    enabled: enabledUserSelect || Boolean(userid),
    select: (response) => ({
      user: response?.data?.user,
      department: response?.data?.department,
    }),
  });

  // ========================================
  // Mutations
  // ========================================
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => userApiService.insertData(payload),
    onSuccess: (data) => {
      showToastMessage("success", data.message);
    },
    onError: (error: any) => {
      showToastMessage("error", error?.message);
    },
  });

  const UpdateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: (payload: any) => userApiService.updateRole(payload),
    onSuccess: (data) => {
      showToastMessage("success", data.message);
    },
    onError: (error: any) => {
      showToastMessage("error", error?.message);
    },
  });

  const DeleteMutation = useMutation<
    ApiResponse,
    Error,
    { id: number | null; userid?: number | null; updatereason: string }
  >({
    mutationFn: (payload) =>
      userApiService.deleteData({
        id: payload.id,
        userid: payload.userid,
        updatereason: payload.updatereason,
      }),
    onSuccess: (data) => {
      showToastMessage("success", data.message);
    },
    onError: (error: any) => {
      showToastMessage("error", error?.message);
    },
  });

  // ========================================
  // Dropdown Queries
  // ========================================

  // "Assignable users" dropdown -> Users/DropdownSSO (or Users/Dropdown when useLocalDropdown is true)
  const UserDropdownQuery = useQuery({
    queryKey: ["user-dropdown-selectall", useLocalDropdown],
    queryFn: () => userApiService.getUserDropdownData(useLocalDropdown),
    enabled: enabledUserDropdown,
  });

  // "Assignable roles" dropdown -> Role/Dropdown, used by both AddUser and UpdateRole
  const RoleDropdownQuery = useQuery({
    queryKey: ["user-role-dropdown"],
    queryFn: () => userApiService.getRoleDropdownData(),
    enabled: enabledRoleDropdown,
  });

  return {
    SelectAllQuery,
    SelectQuery,
    UpdateMutation,
    InsertMutation,
    DeleteMutation,
    RoleDropdownQuery,
    UserDropdownQuery,
  };
};
